(() => {
  'use strict';

  const STORAGE_KEY = 'lobeplugs:favorites:v1';
  const EVENT_NAME = 'lobeplugs:favorites:change';

  const readFavorites = () => {
    try {
      const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(value) ? value : [];
    } catch (error) {
      return [];
    }
  };

  const writeFavorites = (items) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    document.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { items } }));
  };

  const normalizeId = (value) => String(value || '').replace(/\D/g, '');
  const isFavorite = (id) => readFavorites().some((item) => item.id === normalizeId(id));
  const escapeHtml = (value) => String(value || '').replace(/[&<>"']/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;'
  })[character]);

  const recordFromCard = (card) => {
    const link = card.querySelector('a[href*="/products/"]');
    const image = card.querySelector('img');
    const titleNode = card.querySelector('[class*="product-title"], [class*="product-card__title"], h3, h2');
    const priceNode = card.querySelector('[class*="price"]');
    return {
      id: normalizeId(card.dataset.productId),
      designCode: (card.dataset.designCode || '').trim(),
      title: (card.dataset.productTitle || titleNode?.textContent || link?.getAttribute('aria-label') || '').trim(),
      url: card.dataset.productUrl || link?.href || '',
      image: card.dataset.productImage || image?.currentSrc || image?.src || '',
      price: (card.dataset.productPrice || priceNode?.textContent || '').trim(),
      addedAt: new Date().toISOString()
    };
  };

  const recordFromProduct = (root) => {
    const canonical = document.querySelector('link[rel="canonical"]');
    const image = document.querySelector('meta[property="og:image"]');
    const title = document.querySelector('meta[property="og:title"]');
    return {
      id: normalizeId(root.dataset.productId || window.ShopifyAnalytics?.meta?.product?.id),
      designCode: (root.dataset.designCode || window.LobePlugsProduct?.designCode || '').trim(),
      title: (root.dataset.productTitle || title?.content || document.querySelector('h1')?.textContent || '').trim(),
      url: root.dataset.productUrl || canonical?.href || location.href,
      image: root.dataset.productImage || image?.content || '',
      price: (root.dataset.productPrice || document.querySelector('[class*="price"]')?.textContent || '').trim(),
      addedAt: new Date().toISOString()
    };
  };

  const toggle = (record) => {
    if (!record.id) return;
    const items = readFavorites();
    const index = items.findIndex((item) => item.id === record.id);
    const action = index >= 0 ? 'remove' : 'add';
    if (index >= 0) items.splice(index, 1);
    else items.push(record);
    writeFavorites(items);
    document.dispatchEvent(new CustomEvent('lobeplugs:favorites:toggle', {
      detail: {
        action,
        productId: record.id,
        designCode: record.designCode,
        title: record.title
      }
    }));
  };

  const heartSvg = () => '<svg aria-hidden="true" viewBox="0 0 24 24"><path d="M12 20.5S3.5 15.6 3.5 8.9A4.4 4.4 0 0 1 12 7.3a4.4 4.4 0 0 1 8.5 1.6C20.5 15.6 12 20.5 12 20.5Z"/></svg>';

  const updateButton = (button) => {
    const active = isFavorite(button.dataset.productId);
    button.classList.toggle('is-favorite', active);
    button.setAttribute('aria-pressed', String(active));
    button.setAttribute('aria-label', active ? 'Quitar de favoritos' : 'Añadir a favoritos');
    if (button.classList.contains('lobeplugs-favorite-product')) {
      const label = button.querySelector('.lobeplugs-favorite-label');
      if (label) label.textContent = active ? 'En favoritos' : 'Añadir a favoritos';
    }
  };

  const makeButton = (record, productPage) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = productPage ? 'lobeplugs-favorite lobeplugs-favorite-product' : 'lobeplugs-favorite lobeplugs-favorite-card';
    button.dataset.productId = record.id;
    button.innerHTML = heartSvg() + (productPage ? '<span class="lobeplugs-favorite-label"></span>' : '');
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      toggle(record);
      button.classList.remove('is-popping');
      void button.offsetWidth;
      button.classList.add('is-popping');
    });
    updateButton(button);
    return button;
  };

  const enhanceCards = () => {
    document.querySelectorAll('product-card[data-product-id]').forEach((card) => {
      if (card.querySelector('.lobeplugs-favorite-card')) return;
      const record = recordFromCard(card);
      if (!record.id) return;
      card.classList.add('lobeplugs-favorite-host');
      card.appendChild(makeButton(record, false));
    });
  };

  const enhanceProduct = () => {
    const root = document.querySelector('[data-lobeplugs-product][data-product-id]');
    if (!root || root.dataset.favoritesEnhanced === 'true') return;
    const record = recordFromProduct(root);
    const target = document.querySelector('[id^="ProductInformation-"]') || document.querySelector('.product-information') || document.querySelector('main');
    if (!target) return;
    const wrap = document.createElement('div');
    wrap.className = 'lobeplugs-favorite-product-wrap';
    wrap.appendChild(makeButton(record, true));
    target.prepend(wrap);
    root.dataset.favoritesEnhanced = 'true';
  };

  const addHeaderLink = () => {
    if (document.querySelector('.lobeplugs-favorites-link')) return;
    const target = document.querySelector('header a[href*="/account"], header a[href*="/cart"], header button[aria-label*="Cuenta"], header button[aria-label*="Carrito"]');
    if (!target) return;
    const link = document.createElement('a');
    link.href = '/pages/mis-favoritos';
    link.className = 'lobeplugs-favorites-link';
    link.innerHTML = heartSvg() + '<span>Mis favoritos</span><b data-favorites-count>0</b>';
    link.setAttribute('aria-label', 'Abrir Mis favoritos');
    target.parentNode.insertBefore(link, target);
  };

  const updateHeaderCount = () => {
    document.querySelectorAll('[data-favorites-count]').forEach((node) => {
      node.textContent = String(readFavorites().length);
    });
  };

  const ensureFavoritesPage = () => {
    if (location.pathname.replace(/\/$/, '') !== '/pages/mis-favoritos') return;
    if (document.querySelector('[data-lobeplugs-favorites-page]')) return;
    const main = document.querySelector('main');
    if (!main) return;
    main.innerHTML = '<section class="lobeplugs-favorites-page"><h1>Mis favoritos</h1><div data-lobeplugs-favorites-page aria-live="polite"></div></section>';
  };

  const renderFavoritesPage = () => {
    const root = document.querySelector('[data-lobeplugs-favorites-page]');
    if (!root) return;
    const items = readFavorites();
    const signature = JSON.stringify(items.map((item) => item.id));
    if (root.dataset.renderSignature === signature) return;
    root.dataset.renderSignature = signature;
    if (!items.length) {
      root.innerHTML = '<div class="lobeplugs-favorites-empty"><span>♡</span><p>Todavía no tienes diseños favoritos. Explora nuestras colecciones y guarda los que más te gusten.</p><a href="/collections/all">Explorar diseños</a></div>';
      return;
    }
    root.innerHTML = '<div class="lobeplugs-favorites-grid">' + items.map((item) =>
      '<article class="lobeplugs-favorites-item" data-favorite-id="' + escapeHtml(item.id) + '">' +
        '<a class="lobeplugs-favorites-image" href="' + escapeHtml(item.url) + '">' +
          (item.image ? '<img src="' + escapeHtml(item.image) + '" alt="' + escapeHtml(item.title) + '" loading="lazy">' : '') +
        '</a>' +
        '<div class="lobeplugs-favorites-info">' +
          (item.designCode ? '<small>' + escapeHtml(item.designCode) + '</small>' : '') +
          '<h2><a href="' + escapeHtml(item.url) + '">' + escapeHtml(item.title) + '</a></h2>' +
          '<p>' + escapeHtml(item.price) + '</p>' +
          '<div class="lobeplugs-favorites-actions"><a href="' + escapeHtml(item.url) + '">Ver producto</a>' +
          '<button type="button" data-remove-favorite="' + escapeHtml(item.id) + '" aria-label="Quitar de favoritos">' + heartSvg() + '</button></div>' +
        '</div>' +
      '</article>'
    ).join('') + '</div>';
    root.querySelectorAll('[data-remove-favorite]').forEach((button) => {
      button.addEventListener('click', () => {
        const record = readFavorites().find((item) => item.id === button.dataset.removeFavorite);
        if (record) toggle(record);
      });
    });
  };

  const refresh = () => {
    enhanceCards();
    enhanceProduct();
    addHeaderLink();
    document.querySelectorAll('.lobeplugs-favorite').forEach(updateButton);
    updateHeaderCount();
    ensureFavoritesPage();
    renderFavoritesPage();
  };

  let scheduled = false;
  const scheduleRefresh = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      refresh();
    });
  };

  document.addEventListener(EVENT_NAME, refresh);
  window.addEventListener('storage', refresh);
  new MutationObserver(scheduleRefresh).observe(document.documentElement, { childList: true, subtree: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh);
  else refresh();

  window.LobePlugsFavorites = {
    getAll: readFavorites,
    contains: isFavorite,
    toggle,
    eventName: EVENT_NAME
  };
})();
